import { useEffect } from 'react';
import { type FilterGroup, isValidFilterGroup } from '@scale/compass-shared/filter';
import { useProjectId } from 'react-scale/corp/views/CorpProjectGroupManager/hooks/useProjectId';
import _ from 'lodash';
import type { TaskAdapterContext, TaskSearchMode } from '@scale/genai-compass-shared/cards/tasks';

function useTaskAdapterCard(
  context: TaskAdapterContext,
  updateCardContext: (context: TaskAdapterContext) => void,
  updateCardCanSubmit: (canSubmit: boolean) => void,
) {
  const projectId = useProjectId();

  const setFilter = (filter: FilterGroup) => {
    const updatedContext: TaskAdapterContext = {
      ...context,
      filter,
    };
    updateCardContext(updatedContext);
  };

  const setProjectId = (projectId: string) => {
    const updatedContext: TaskAdapterContext = {
      ...context,
      projectId,
    };
    updateCardContext(updatedContext);
  };

  const setSearchMode = (searchMode: TaskSearchMode) => {
    const updatedContext: TaskAdapterContext = {
      ...context,
      searchMode,
    };
    updateCardContext(updatedContext);
  };

  const setTaskIds = (taskIds: string[]) => {
    const updatedContext: TaskAdapterContext = {
      ...context,
      taskIds,
    };
    updateCardContext(updatedContext);
  };

  const setLimit = (limitStr: string | undefined | null) => {
    let limit: number | undefined;
    if (_.isNil(limitStr) || limitStr === '') {
      limit = undefined;
    } else {
      limit = Math.max(1, +limitStr);
    }

    if (_.isNil(limit) || (limit && limit > 0)) {
      const updatedContext: TaskAdapterContext = {
        ...context,
        limit,
      };
      updateCardContext(updatedContext);
    }
  };

  useEffect(() => {
    let canSubmit = true;

    if (context.searchMode === 'Search') {
      if (!context.taskIds?.length) {
        canSubmit = false;
      }
    } else {
      // searchMode is Filter or undefined
      canSubmit = isValidFilterGroup(context.filter);
    }

    updateCardCanSubmit(canSubmit);
  }, [context.searchMode, context.filter, context.taskIds]);

  useEffect(() => {
    if (projectId) {
      const updatedContext: TaskAdapterContext = {
        ...context,
        projectId,
      };
      updateCardContext(updatedContext);
    }
  }, [projectId]);

  return {
    filter: context.filter,
    setFilter,
    searchMode: context.searchMode,
    setSearchMode,
    taskIds: context.taskIds,
    setTaskIds,
    limit: context.limit,
    setLimit,
    projectId: context.projectId,
    setProjectId,
    allowEditProjectId: !projectId,
  };
}

export default useTaskAdapterCard;
